
import { useState, useEffect, useRef } from 'react';
import { Canvas } from 'fabric';
import { ActionStack } from './undo-redo/ActionStack';

export const useUndoRedo = (canvas: Canvas | null) => {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const actionStackRef = useRef<ActionStack | null>(null);

  useEffect(() => {
    if (!canvas) return;
    
    // Initialize action stack
    actionStackRef.current = new ActionStack(canvas);
    
    // Update undo/redo state
    const updateUndoRedoState = () => {
      if (actionStackRef.current) {
        setCanUndo(actionStackRef.current.canUndo());
        setCanRedo(actionStackRef.current.canRedo());
      }
    };
    
    // Listen for canvas changes to update undo/redo state
    const events = ['object:added', 'object:removed', 'object:modified'];
    events.forEach(event => {
      // Use type assertion to handle event name properly
      canvas.on(event as any, updateUndoRedoState);
    });
    
    // Initial state
    updateUndoRedoState();
    
    return () => {
      // Clean up event listeners
      events.forEach(event => {
        // Use type assertion to handle event name properly
        canvas.off(event as any, updateUndoRedoState);
      });
    };
  }, [canvas]);

  const undo = () => {
    if (actionStackRef.current && actionStackRef.current.canUndo()) {
      const result = actionStackRef.current.undo();
      setCanUndo(actionStackRef.current.canUndo());
      setCanRedo(actionStackRef.current.canRedo());
      return result;
    }
    return false;
  };

  const redo = () => {
    if (actionStackRef.current && actionStackRef.current.canRedo()) {
      const result = actionStackRef.current.redo();
      setCanUndo(actionStackRef.current.canUndo());
      setCanRedo(actionStackRef.current.canRedo());
      return result;
    }
    return false;
  };

  const clearHistory = () => {
    if (actionStackRef.current) {
      actionStackRef.current.clear();
      setCanUndo(false);
      setCanRedo(false);
    }
  };

  return {
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory
  };
};
